import { PosStorageKey } from './../../../pos/services/storage.key';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthHTTPService } from './auth-http/auth-http.service';
import { TranslateService } from '@ngx-translate/core';
import { MenuConfig } from 'src/app/configs/menu-config';
import { StorageService } from 'src/app/pos/services/storage.service';
import { MobilePermission } from 'src/app/pos/models/permission.model';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  public currentToken: string | null;
  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  permissions: any = [];
  public menuConfig: any;

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private translate: TranslateService,
    private storageService: StorageService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);

    this.currentToken = sessionStorage.getItem('token');

    this.permissions = JSON.parse(sessionStorage.getItem('permissions')) || [];

    // let json = JSON.stringify(new MenuConfig().configs);
    // this.menuConfig = JSON.parse(json);
  }

  getToken() {
    return this.currentToken;
  }

  isAuthenticated() {
    return this.currentToken ? true : false;
  }

  setToken(token: string) {
    this.currentToken = token;
    if (token) {
      sessionStorage.setItem('token', token);
    } else {
      sessionStorage.removeItem('token');
    }
  }

  // public methods
  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    sessionStorage.removeItem('token');
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.forgotPassword(email).pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      sessionStorage.setItem('token', JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    return undefined;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  // PERMISSION
  setPermissions(perms) {
    // set for mobile permissions
    const mobilePermissionActions = [
      'การจัดการหน้าขาย',
      'ขาย-ขาย',
      'ขาย-โปรโมชั่น/ส่วนลดพิเศษ',
      'ขาย-ชำระเงิน',
      'ขาย-เปลี่ยนแปลงจำนวนน้อยลงหรือยกเลิกรายการสินค้า',
      'บิล-บิลทั้งหมด',
      'ใบเสร็จ-ยกเลิกใบเสร็จ',
      'ใบเสร็จ-พิมพ์ใบเสร็จ',
      'ใบเสร็จ-พิมฟ์ใบกำกับภาษี',
      'เพิ่มรายการสินค้า',
      'สร้างลูกค้า',
    ];
    const mobilePermissionList = perms.filter((x) =>
      mobilePermissionActions.includes(x.permission_module_name)
    ) as MobilePermission[];
    this.storageService.set(PosStorageKey.permission, mobilePermissionList);

    // end of permission for mobile

    let permissions = [];
    // permissions.push({ action: 'ดู', group: null, permission: 'ภาพรวม' });

    perms.forEach((p) => {
      permissions.push({
        action: p.permission_name,
        group: this.getMenuGroup(p.permission_module_name),
        permission: p.permission_module_name,
      });
    });

    this.permissions = permissions;
    if (permissions) {
      sessionStorage.setItem('permissions', JSON.stringify(permissions));
    } else {
      sessionStorage.removeItem('permissions');
    }
  }

  hasPermission(title, action = null, group?) {
    let p = this.permissions.filter((p: any) => {
      if (title && action) {
        return p.permission === title && this.getAction(action) === p.action;
      } else {
        let str = p.group;
        return str === group;
      }
    });
    return p.length > 0;
  }

  getAction(action) {
    switch (action) {
      case 'view':
        return 'ดู';
      case 'create':
        return 'เพิ่ม';
      case 'update':
        return 'แก้ไข';
      case 'delete':
        return 'ลบ';
      case 'reverse':
        return 'Reverse/ยกเลิก/ปิดรุ่น';
      case 'POS':
        return 'เข้าถึงpos';
      default:
        return action;
    }
  }

  getMenuGroup(perm_menu) {
    if (['ข้อมูลสมาชิก', 'ข้อมูลฟาร์ม', 'การรับส่งข้อความ'].indexOf(perm_menu) > -1) {
      return 'สมาชิก';
    }
    if (
      [
        'สั่งซื้อ',
        'ซื้อวัตถุดิบ/สินค้า',
        'เคลมวัตถุดิบ/สินค้า',
        'รับเข้าวัตถุดิบ/สินค้า',
        'เบิกวัตถุดิบ/สินค้า',
        'คืนวัตถุดิบ/สินค้า',
        'ปรับปรุงสต็อก',
        'เสนอราคา',
        'ขายวัตถุดิบ/ผลผลิต/อุปกรณ์',
        'รับซื้อผลผลิต',
        'บันทึกส่งเคลมคืน',
        'บันทึกมัดจำ',
        'บันทึกรับเคลมคืน (ลูกค้า/เกษตรกร)',
        'ลดหนี้',
        'เพิ่มหนี้',
        'ส่งของ',
        'บันทึกส่งสินค้าที่เคลมให้ลูกค้า',
      ].indexOf(perm_menu) > -1
    ) {
      return 'คงคลัง';
    }
    if (
      [
        'ข้อมูลค่าใช้จ่าย',
        'ข้อมูลการรับชำระหนี้',
        'ข้อมูลการจ่ายชำระหนี้',
        'ข้อมูลค่าจ้างพนักงาน',
        'ข้อมูลจ่ายเงินปันผลสมาชิก',
        'ข้อมูลรายได้อื่น',
      ].indexOf(perm_menu) > -1
    ) {
      return 'บัญชี';
    }
    if (['ประเภทกิจกรรม', 'กิจกรรมการผลิต', 'รูปแบบการผลิต', 'บันทึกกิจกรรม'].indexOf(perm_menu) > -1) {
      return 'การผลิต';
    }
    if (['ข้อมูลสัญญา', 'ข้อมูลรุ่น'].indexOf(perm_menu) > -1) {
      return 'สัญญา/รุ่น';
    }
    if (
      [
        'ข้อมูลทั่วไป',
        'ข้อมูลลูกค้า',
        'ข้อมูลผู้จำหน่าย',
        'ข้อมูลบัญชีธนาคาร',
        'รายการค่าใช้จ่าย',
        'รายการรายได้อื่น',
      ].indexOf(perm_menu) > -1
    ) {
      return 'ตั้งค่า';
    }
    if (
      [
        'รายงานต้นทุนผลิตรวม',
        'รายงานต้นทุนผลิตแยกตามประเภทกิจกรรม',
        'รายการให้อาหาร/ใส่ปุ๋ย/ยา',
        'รายงานค่าการเจริญเติบโต',
        'รายงานปริมาณผลผลิต',
        'รายงานการสูญเสีย',
      ].indexOf(perm_menu) > -1
    ) {
      return 'รายงาน';
    } else {
      return null;
    }
  }
}
